import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AppWindow, Edit, Eye, PauseCircle, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Breadcrumbs, DetailList, Panel, StatTile } from '@/components/ui/detail-list'
import { SoftwareForm } from '@/components/forms/SoftwareForm'
import { SoftwareAccountForm } from '@/components/forms/SoftwareAccountForm'
import { SoftwareAccountStatusModal } from '@/components/modals/SoftwareAccountStatusModal'
import { apiClient } from '@/lib/api-client'
import { accountStatusColor, accountStatusLabel, billingLabel } from '@/lib/software-format'
import { formatDateShort } from '@shared/utils'
import { toast } from 'react-hot-toast'

export function SoftwareDetailPage() {
  const { softwareId } = useParams()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [adding, setAdding] = useState(false)
  const [editAccount, setEditAccount] = useState<any>(null)
  const [statusAccount, setStatusAccount] = useState<any>(null)

  const { data: software, isLoading } = useQuery({
    queryKey: ['software', softwareId],
    queryFn: async () => (await apiClient.get(`/software/${softwareId}`)).data,
    enabled: !!softwareId,
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['software'] })
  }

  const deleteAccount = useMutation({
    mutationFn: async (accountId: number) => apiClient.delete(`/software/accounts/${accountId}`),
    onSuccess: () => {
      refresh()
      toast.success('Paid account deleted')
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Delete failed'),
  })

  const accounts: any[] = software?.accounts || []
  const counts = useMemo(() => accounts.reduce((acc, account) => {
    acc.total += 1
    acc.users += account.memberCount || 0
    if (account.status === 'active') acc.active += 1
    else if (account.status === 'expired') acc.expired += 1
    else acc.cancelled += 1
    return acc
  }, { total: 0, active: 0, expired: 0, cancelled: 0, users: 0 }), [accounts])

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center"><LoadingSpinner size="lg" /></div>
  }

  if (!software) {
    return (
      <div className="py-16 text-center">
        <AppWindow className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <p className="text-sm font-medium">Software not found</p>
        <Button asChild variant="outline" className="mt-4"><Link to="/software">Back to software</Link></Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Breadcrumbs items={[{ label: 'Software', to: '/software' }, { label: software.name }]} />

      <div className="flex flex-col gap-3 border-b border-border/70 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold tracking-tight">{software.name}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditing(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit software
          </Button>
          <Button onClick={() => setAdding(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add account
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Accounts" value={counts.total} />
        <StatTile label="Active" value={counts.active} tone="text-emerald-600 dark:text-emerald-400" />
        <StatTile label="Expired" value={counts.expired} tone="text-amber-600 dark:text-amber-400" />
        <StatTile label="Users" value={counts.users} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Software details" className="self-start" actions={
          <Button variant="ghost" size="sm" className="h-8" onClick={() => setEditing(true)}>
            <Edit className="mr-1.5 h-3.5 w-3.5" />
            Edit
          </Button>
        }>
          <DetailList items={[
            { label: 'Vendor', value: software.vendor },
            {
              label: 'Website',
              value: software.website ? (
                <a href={software.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">{software.website}</a>
              ) : null,
            },
            { label: 'Description', value: software.description },
            { label: 'Added', value: software.createdAt ? formatDateShort(software.createdAt) : null },
          ]} />
        </Panel>

        <Panel title={`Paid accounts (${accounts.length})`} className="lg:col-span-2">
          {accounts.length === 0 ? (
            <p className="px-4 py-12 text-center text-sm text-muted-foreground">No paid accounts yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Plan</th>
                    <th>Users</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.id}>
                      <td>
                        <Link to={`/software/accounts/${account.id}`} className="text-sm font-medium text-primary hover:underline">
                          {account.accountName}
                        </Link>
                        <p className="truncate text-xs text-muted-foreground">{account.email}</p>
                      </td>
                      <td data-label="Plan">
                        <p className="text-sm">{account.plan || '—'}</p>
                        <p className="text-xs text-muted-foreground">{billingLabel(account)}</p>
                      </td>
                      <td data-label="Users">{account.memberCount || 0}</td>
                      <td data-label="Status">
                        <Badge className={accountStatusColor(account.status)}>{accountStatusLabel(account.status)}</Badge>
                        {account.statusReason && (
                          <p className="mt-1 max-w-[16rem] text-xs text-muted-foreground">{account.statusReason}</p>
                        )}
                      </td>
                      <td className="is-actions">
                        <div className="flex justify-end gap-1">
                          <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                            <Link to={`/software/accounts/${account.id}`} aria-label="View account"><Eye className="h-4 w-4" /></Link>
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Update status" title="Update status" onClick={() => setStatusAccount(account)}>
                            <PauseCircle className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Edit account" onClick={() => setEditAccount(account)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            aria-label="Delete account"
                            onClick={() => {
                              if (window.confirm(`Delete paid account ${account.email}?`)) deleteAccount.mutate(account.id)
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <SoftwareForm
        isOpen={editing}
        softwareId={editing ? software.id : null}
        onClose={() => setEditing(false)}
        onSuccess={refresh}
      />
      <SoftwareAccountForm
        isOpen={adding || !!editAccount}
        softwareId={software.id}
        account={editAccount}
        onClose={() => { setAdding(false); setEditAccount(null) }}
        onSuccess={refresh}
      />
      <SoftwareAccountStatusModal
        isOpen={!!statusAccount}
        account={statusAccount}
        onClose={() => setStatusAccount(null)}
        onSuccess={refresh}
      />
    </div>
  )
}
